<template>
  <div class="form-control w-full space-y-1">
    <Datepicker
      v-bind="$attrs"
      v-model="inputValue"
      :format="dateFormat"
      :enable-time-picker="!!enableTimePicker"
      :label="label"
      :tooltip="tooltip"
      :is-error="!!error"
      :hint="error"
    />
    <AiValidation :field-name="props.name" :parse-value="parseValue">
      <template #displayValue="value">
        {{
          calendarDate
            ? formatCalendarDate(value.value, dateFormat)
            : formatDate(value.value, dateFormat)
        }}
      </template>
    </AiValidation>
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, watch } from 'vue';
import Datepicker from './Datepicker.vue';
import { useDateFormat, useDateTimeFormat } from '@/hooks/use-date-format';
import AiValidation from './AiValidation.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import {
  formatCalendarDate,
  fromCalendarDate,
  toCalendarDate,
  toLocalDateForPicker,
} from '@/utils/calendar-date';

const props = defineProps<{
  label?: string;
  hint?: string;
  name: string;
  initialValue?: string | Date | null;
  enableTimePicker?: boolean;
  /** Birthdays and document dates — stored as UTC midnight for the picked calendar day. */
  calendarDate?: boolean;
  tooltip?: string;
}>();

const parseValue = (value: string) => {
  const date = new Date(value);
  return props.calendarDate ? fromCalendarDate(date) : date;
};

const dateFormat = props.enableTimePicker ? useDateTimeFormat() : useDateFormat();

const normalizeFieldValue = (
  value: string | Date | null | undefined,
  convertCalendarDate: (date: Date) => Date,
): Date | string | null | undefined => {
  if (!value) return value;
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return date;
  return props.calendarDate ? convertCalendarDate(date) : date;
};

const { value: fieldValue, errorMessage: error } = useField<Date | string | null | undefined>(
  props.name,
  undefined,
  {
    initialValue: normalizeFieldValue(props.initialValue, fromCalendarDate),
  },
);

const inputValue = computed({
  get: () => {
    if (!fieldValue.value) return null;
    return props.calendarDate
      ? toLocalDateForPicker(fieldValue.value as Date | string)
      : fieldValue.value;
  },
  set: (value: Date | string | null) => {
    if (!value) {
      fieldValue.value = value;
      return;
    }
    fieldValue.value = normalizeFieldValue(value, toCalendarDate) as Date | string;
  },
});

watch(
  fieldValue,
  (value) => {
    if (!props.calendarDate || !value) return;
    const normalized = normalizeFieldValue(value, fromCalendarDate);
    const valueTime = new Date(value as Date | string).getTime();
    const normalizedTime = new Date(normalized as Date | string).getTime();
    // Also replace date-only strings: yup casts "YYYY-MM-DD" to local midnight on submit.
    const needsNormalizing = typeof value === 'string' || normalizedTime !== valueTime;
    if (!Number.isNaN(valueTime) && !Number.isNaN(normalizedTime) && needsNormalizing) {
      fieldValue.value = normalized as Date;
    }
  },
  { immediate: true },
);
</script>
